import {
  QuestionnaireInterface,
  QuestionnaireResponse,
  ThoughtInterface,
  UserAssessmentResponse,
} from '@api/assessmentQuestionnaireApiHandler';
import { ScoringType } from '@enums/ScoringTypes';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';

const initialState: QuestionnaireState = {
  chosenQuestionnaire: null,
  currentQuestionIndex: 0,
  answers: {},
  questionnaire: null,
  isBreakthrough: false,
  navigationStep: 'scoring',
  createdAssessmentDetails: null,
  thoughtsList: [],
  scoringType: ScoringType.LIKERT_ONE_QUESTION,
  focusAreas: [],
};

const questionnaireSlice = createSlice({
  name: 'questionnaire',
  initialState,
  reducers: {
    setChosenQuestionnaire: (state, action: PayloadAction<QuestionnaireResponse | null>) => {
      const isDesktop = window.innerWidth > 992;
      const incoming = action.payload?.questionnaireV2?.scoringType;

      // Mobile remap only for classic multi-Likert (not with-comments — that keeps its own 1Q mobile paging).
      let resolvedScoringType = incoming || ScoringType.LIKERT_ONE_QUESTION;
      if (incoming === ScoringType.LIKERT_MULTIPLE_QUESTIONS) {
        resolvedScoringType = isDesktop
          ? ScoringType.LIKERT_MULTIPLE_QUESTIONS
          : ScoringType.LIKERT_ONE_QUESTION;
      }

      return {
        ...state,
        chosenQuestionnaire: action.payload,
        scoringType: resolvedScoringType,
      };
    },

    setQuestionnaireQuestions: (state, action: PayloadAction<QuestionnaireInterface | null>) => ({
      ...state,
      questionnaire: action.payload,
      currentQuestionIndex: 0,
      answers: {},
      isBreakthrough: false,
    }),

    answerQuestion: (
      state,
      action: PayloadAction<{ questionId: number; answerId: number; answerScore: number }>,
    ) => {
      const currentQuestion =
        state.questionnaire?.questionnairesV2Questions[state.currentQuestionIndex];

      return {
        ...state,
        answers: {
          ...state.answers,
          [action.payload.questionId]: {
            questionId: action.payload.questionId,
            answerId: action.payload.answerId,
            answerScore: action.payload.answerScore,
          },
        },
        isBreakthrough: currentQuestion ? currentQuestion.isBrakeOff : false,
        currentQuestionIndex:
          state.currentQuestionIndex <
          (state.questionnaire?.questionnairesV2Questions.length ?? 0) - 1
            ? state.currentQuestionIndex + 1
            : state.currentQuestionIndex,
      };
    },

    nextQuestion: (state) => {
      if (
        state.questionnaire &&
        state.currentQuestionIndex < state.questionnaire.questionnairesV2Questions.length - 1
      ) {
        const newIndex = state.currentQuestionIndex + 1;

        return {
          ...state,
          currentQuestionIndex: newIndex,
          isBreakthrough: state.questionnaire.questionnairesV2Questions[newIndex].isBrakeOff,
        };
      }

      return state;
    },

    previousQuestion: (state) => {
      if (state.currentQuestionIndex > 0) {
        const newIndex = state.currentQuestionIndex - 1;

        return {
          ...state,
          currentQuestionIndex: newIndex,
          isBreakthrough:
            state.questionnaire?.questionnairesV2Questions[newIndex]?.isBrakeOff ?? false,
        };
      }

      return state;
    },

    setNavigationStep: (state, action: PayloadAction<AssessmentNavigationTypes>) => ({
      ...state,
      navigationStep: action.payload,
    }),

    setCreatedUserAssessment: (state, action: PayloadAction<UserAssessmentResponse>) => ({
      ...state,
      createdAssessmentDetails: action.payload,
    }),

    addThought: (state, action: PayloadAction<ThoughtInterface>) => ({
      ...state,
      thoughtsList:
        state.thoughtsList.findIndex(
          (thought) => thought.questionId === action.payload.questionId,
        ) >= 0
          ? state.thoughtsList.map((thought) =>
              thought.questionId === action.payload.questionId ? action.payload : thought,
            )
          : [...state.thoughtsList, action.payload],
    }),

    answerLikertMultipleQuestion: (
      state: QuestionnaireState,
      action: PayloadAction<{ questionId: number; answerId: number; answerScore: number }>,
    ) => ({
      ...state,
      answers: {
        ...state.answers,
        [action.payload.questionId]: {
          questionId: action.payload.questionId,
          answerId: action.payload.answerId,
          answerScore: action.payload.answerScore,
        },
      },
    }),

    answerLikertWithCommentsQuestion: (
      state: QuestionnaireState,
      action: PayloadAction<{
        questionId: number;
        selectedOptionIds: number[];
        answerScore: number;
        commentText?: string | null;
      }>,
    ) => {
      const existing = state.answers[String(action.payload.questionId)];

      return {
        ...state,
        answers: {
          ...state.answers,
          [action.payload.questionId]: {
            questionId: action.payload.questionId,
            answerId: action.payload.selectedOptionIds[0] ?? 0,
            answerScore: action.payload.answerScore,
            selectedOptionIds: action.payload.selectedOptionIds,
            commentText:
              action.payload.commentText !== undefined
                ? action.payload.commentText
                : existing?.commentText ?? null,
          },
        },
      };
    },

    setLikertWithCommentsComment: (
      state: QuestionnaireState,
      action: PayloadAction<{ questionId: number; commentText: string }>,
    ) => {
      const existing = state.answers[String(action.payload.questionId)];

      return {
        ...state,
        answers: {
          ...state.answers,
          [action.payload.questionId]: {
            questionId: action.payload.questionId,
            answerId: existing?.answerId ?? 0,
            answerScore: existing?.answerScore ?? 0,
            selectedOptionIds: existing?.selectedOptionIds ?? [],
            commentText: action.payload.commentText,
          },
        },
      };
    },

    setActiveFocusAreas: (state, action: PayloadAction<ConvertedFocusArea[]>) => ({
      ...state,
      focusAreas: action.payload,
    }),

    resetQuestionnaire: () => initialState, // Resets to initial state
  },
});

interface QuestionnaireState {
  chosenQuestionnaire: QuestionnaireResponse | null;
  currentQuestionIndex: number;
  answers: Record<string, UserAnswer>;
  questionnaire: QuestionnaireInterface | null;
  isBreakthrough: boolean;
  navigationStep: AssessmentNavigationTypes;
  createdAssessmentDetails: UserAssessmentResponse | null;
  thoughtsList: ThoughtInterface[] | [];
  scoringType:
    | ScoringType.CHECK_IN
    | ScoringType.LIKERT_MULTIPLE_QUESTIONS
    | ScoringType.LIKERT_MULTIPLE_QUESTIONS_WITH_COMMENTS
    | ScoringType.LIKERT_ONE_QUESTION;
  focusAreas: ConvertedFocusArea[];
}

export type AssessmentNavigationTypes =
  | 'checkIn'
  | 'scoring'
  | 'ewsTrigger'
  | 'talkToAdvisor'
  | 'reflection'
  | 'focusAreas'
  | 'complete';

export interface UserAnswer {
  questionId: number;
  answerId: number;
  answerScore: number;
  /** Used by likertMultipleQuestionsWithComments (single/multiple/none). */
  selectedOptionIds?: number[];
  commentText?: string | null;
}

export interface ConvertedFocusArea {
  id: number;
  category: string;
  subCategory: string;
  title: string;
  score: string;
  scoreClass: string;
  icon: string | undefined;
  priorityScore: number;
  concernPercentage: number;
  questionV2Id: number;
  focusAreaId: number;
  iconUrl: string | null;
}

export const {
  answerQuestion,
  nextQuestion,
  previousQuestion,
  resetQuestionnaire,
  setQuestionnaireQuestions,
  setChosenQuestionnaire,
  setNavigationStep,
  setCreatedUserAssessment,
  addThought,
  answerLikertMultipleQuestion,
  answerLikertWithCommentsQuestion,
  setLikertWithCommentsComment,
  setActiveFocusAreas,
} = questionnaireSlice.actions;

export default questionnaireSlice;
